import React, { useState, useRef, useEffect } from 'react';
import type { PresenceUser } from '../../hooks/usePresence';

const AVATAR_COLORS = [
  '#3b82f6', '#10b981', '#8b5cf6', '#ec4899',
  '#f59e0b', '#ef4444', '#6366f1', '#14b8a6',
  '#f97316', '#06b6d4',
];

function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface PresenceIndicatorProps {
  users: PresenceUser[];
}

export function PresenceIndicator({ users }: PresenceIndicatorProps) {
  const [showList, setShowList] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowList(false);
      }
    }
    if (showList) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showList]);

  if (users.length === 0) return null;

  const displayedUser = users[0];
  const remainingCount = users.length - 1;

  return (
    <div
      ref={containerRef}
      className="relative flex items-center"
      onMouseEnter={() => setShowList(true)}
      onMouseLeave={() => setShowList(false)}
      onClick={() => setShowList(prev => !prev)}
    >
      <div className="flex -space-x-1.5 cursor-pointer">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium ring-2 ring-white"
          style={{ backgroundColor: getUserColor(displayedUser.userId) }}
          title={displayedUser.userName}
        >
          {displayedUser.userInitial}
        </div>
        {remainingCount > 0 && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-200 text-gray-600 text-xs font-medium ring-2 ring-white">
            +{remainingCount}
          </div>
        )}
      </div>

      {showList && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 px-3 min-w-[160px] z-50">
          <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">
            Editing now
          </div>
          {users.map(user => (
            <div key={user.userId} className="flex items-center gap-2 py-1">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0"
                style={{ backgroundColor: getUserColor(user.userId) }}
              >
                {user.userInitial}
              </div>
              <span className="text-sm text-gray-700 truncate">{user.userName}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
